import { Cancel } from "@mui/icons-material";
import moment from "moment";
import { observer } from "mobx-react";
import { useState } from "react";
import type { LLMClassifierImplementation } from "models/LLMs/LLMClassifierImplementation";
import {
    ApprovalEvent,
    ApprovalStatus,
} from "models/LLMs/LLMClassifierApproval";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../../Stores/LLMClassifierBuilderStore";
import { LLMClassifierBuilderStepperStore } from "../../Stores/LLMClassifierBuilderStepperStore";
import {
    canReviewApprovalRequest,
    effectiveApprovalStatus,
    isApprovalRequester,
} from "../../utils/llmClassifierApproval";
import {
    approvalComparisonVersion,
    isLLMClassifierVersionPublished,
    llmClassifierVersionLabel,
} from "../../utils/llmClassifierVersionDisplay";
import { ApprovalEmailNoticeDialog } from "./ApprovalEmailNoticeDialog";
import { ApprovalStatusPanel } from "./ApprovalStatusPanel";
import { ChangeRecordDialog } from "./ChangeRecordDialog";
import { RequestApprovalDialog } from "./RequestApprovalDialog";
import { ReviewApprovalDialog } from "./ReviewApprovalDialog";
import { DecisionDialog } from "./DecisionDialog";
import { useApprovalEmailNotice } from "./useApprovalEmailNotice";
import { useApprovalWorkflowDialogs } from "./useApprovalWorkflowDialogs";

export const LLMClassifierApprovalWorkflow = observer(function (props: {
    version: LLMClassifierImplementation;
    isCondensed?: boolean;
    actionDisabledReason?: string;
}) {
    const store = useStore(LLMClassifierBuilderStore);
    const stepper = useStore(LLMClassifierBuilderStepperStore);
    const version = props.version;
    const status = effectiveApprovalStatus(version.approvalStatus);
    const request = version.approvalRequest;

    const recordedComparisonVersion = request?.comparedToEntityId
        ? store.llmClassifierVersions.find(
              (candidate) => candidate.id === request.comparedToEntityId,
          )
        : undefined;
    const currentComparisonVersion = approvalComparisonVersion(
        store.llmClassifierVersions,
        store.classifier.isPublished,
        version.id,
    );
    const comparisonVersion =
        status === ApprovalStatus.AwaitingApproval
            ? recordedComparisonVersion ?? currentComparisonVersion
            : currentComparisonVersion;

    const currentUserEmail = store.authStore._user.profile.email;
    const currentUserId = store.authStore._user.profile.sub;
    const canReview = store.authStore.canUserEdit("Approver");
    const requester = isApprovalRequester(
        version.approvalRequest,
        currentUserEmail,
    );
    const versionLabel =
        llmClassifierVersionLabel(version.createdOn) ?? "version";
    const requesterName =
        request?.createdByName || request?.createdBy || "the requester";
    const publishedVersionLabel = comparisonVersion
        ? llmClassifierVersionLabel(comparisonVersion.createdOn)
        : undefined;

    const {
        dialog,
        setDialog,
        closeDialog,
        openRequest,
        reviewers,
        setReviewers,
        note,
        setNote,
        reason,
        setReason,
        approverName,
        setApproverName,
        rejecterName,
        setRejecterName,
        isSaving,
        isLoadingReviewers,
        submit,
    } = useApprovalWorkflowDialogs(store, comparisonVersion?.id);

    const nameConfirmed = approverName.trim().length > 0;
    const [isChangeRecordOpen, setIsChangeRecordOpen] = useState(false);

    const { notice, dismissNotice } = useApprovalEmailNotice();
    const latestEvent = version.latestApprovalEvent;
    // Hide the email notice if the version has moved past the event the email was about.
    const showNotice = notice !== undefined && latestEvent?.event === notice;
    const publishedVersion = store.llmClassifierVersions.find((candidate) =>
        isLLMClassifierVersionPublished(store.classifier.isPublished, candidate),
    );

    function noticeAction() {
        switch (latestEvent?.event) {
            case ApprovalEvent.ApprovalRequested:
                return canReviewApprovalRequest({
                    canReview,
                    isRequester: requester,
                    request,
                    currentUserId,
                })
                    ? {
                          label: "Review version",
                          // The approval panel enables review after the diff has been read.
                          onClick: dismissNotice,
                      }
                    : undefined;
            case ApprovalEvent.Approved:
                return {
                    label: "View change record",
                    onClick: () => {
                        dismissNotice();
                        setIsChangeRecordOpen(true);
                    },
                };
            case ApprovalEvent.ChangesRequested:
                return {
                    label: "Edit version",
                    onClick: () => {
                        dismissNotice();
                        stepper.setCurrentWizardStep("builder");
                    },
                };
            case ApprovalEvent.Rejected:
                // The reviewer who rejected it cannot also request its next approval.
                return isApprovalRequester(latestEvent, currentUserEmail)
                    ? undefined
                    : {
                          label: "Request approval",
                          onClick: () => {
                              dismissNotice();
                              openRequest();
                          },
                      };
            default:
                return undefined;
        }
    }

    return (
        <>
            <ApprovalStatusPanel
                version={version}
                status={status}
                isCondensed={props.isCondensed}
                actionDisabledReason={props.actionDisabledReason}
                currentUserEmail={currentUserEmail}
                currentUserId={currentUserId}
                canReview={canReview}
                requester={requester}
                isSaving={isSaving}
                onRequestApproval={openRequest}
                onCancelRequest={() =>
                    submit(ApprovalEvent.ApprovalRequestCanceled)
                }
                onReviewVersion={() => {
                    setApproverName("");
                    setDialog("review");
                }}
                onViewChangeRecord={() => setIsChangeRecordOpen(true)}
            />

            {showNotice && latestEvent && (
                <ApprovalEmailNoticeDialog
                    isOpen
                    onClose={dismissNotice}
                    event={latestEvent.event}
                    actorName={
                        latestEvent.createdByName ||
                        latestEvent.createdBy ||
                        "A reviewer"
                    }
                    eventDate={moment
                        .utc(latestEvent.createdOn)
                        .local()
                        .format("MMM D · HH:mm")}
                    comment={latestEvent.comment}
                    versionLabel={versionLabel}
                    liveVersionLabel={
                        publishedVersion && publishedVersion.id !== version.id
                            ? llmClassifierVersionLabel(
                                  publishedVersion.createdOn,
                              )
                            : undefined
                    }
                    isThisVersionLive={publishedVersion?.id === version.id}
                    action={noticeAction()}
                />
            )}

            <ChangeRecordDialog
                isOpen={isChangeRecordOpen}
                onClose={() => setIsChangeRecordOpen(false)}
                implementationId={version.id}
                versionLabel={versionLabel}
            />

            <RequestApprovalDialog
                isOpen={dialog === "request"}
                onClose={closeDialog}
                availableReviewers={store.approvalReviewers}
                selectedReviewers={reviewers}
                onReviewersChange={setReviewers}
                note={note}
                onNoteChange={setNote}
                before={comparisonVersion}
                after={version}
                isLoadingReviewers={isLoadingReviewers}
                isSaving={isSaving}
                onSubmit={() =>
                    submit(ApprovalEvent.ApprovalRequested, {
                        reviewerIds: reviewers.map(
                            (reviewer) => reviewer.userId,
                        ),
                        comment: note,
                    })
                }
            />

            <ReviewApprovalDialog
                isOpen={dialog === "review"}
                onClose={closeDialog}
                versionLabel={versionLabel}
                approverName={approverName}
                onApproverNameChange={setApproverName}
                isSaving={isSaving}
                canApprove={nameConfirmed}
                onReject={() => {
                    setRejecterName("");
                    setDialog("reject");
                }}
                onRequestChanges={() => setDialog("changes")}
                onApprove={() => submit(ApprovalEvent.Approved)}
            />

            <DecisionDialog
                open={dialog === "reject"}
                title={`Reject ${versionLabel}`}
                titleIcon={
                    <Cancel sx={{ color: "error.main", fontSize: 22 }} />
                }
                description={`Rejecting closes this approval request and marks the version Rejected. Unlike requesting changes, ${requesterName} will have to submit a new request to get it reviewed again.`}
                label="Reason for rejection"
                reasonCaption="Required. Sent to the requester and written to the change record."
                confirmLabel="Reject version"
                reason={reason}
                onReasonChange={setReason}
                onClose={() => setDialog("review")}
                onConfirm={() =>
                    submit(ApprovalEvent.Rejected, { comment: reason })
                }
                loading={isSaving}
                destructive
                signedByLabel="Rejected by"
                signedByValue={rejecterName}
                onSignedByChange={setRejecterName}
                signedDateLabel="Date rejected"
                banner={
                    publishedVersionLabel
                        ? `${publishedVersionLabel} stays live and keeps running. Nothing about the published model changes.`
                        : undefined
                }
            />

            <DecisionDialog
                open={dialog === "changes"}
                title="Request changes"
                description="The author is notified and your reason is written to the change record."
                label="Reason"
                confirmLabel="Request changes"
                reason={reason}
                onReasonChange={setReason}
                onClose={() => setDialog("review")}
                onConfirm={() =>
                    submit(ApprovalEvent.ChangesRequested, { comment: reason })
                }
                loading={isSaving}
            />
        </>
    );
});
